import {
  Brain,
  Stethoscope,
  ShieldCheck,
  Receipt,
  Calendar,
  FileSignature,
  Sparkles,
  Bell,
  ClipboardCheck,
  Building2,
} from "lucide-react";
import {
  Container,
  Section,
  Heading,
  Lede,
  Eyebrow,
  Card,
  LinkButton,
} from "../components/ui";
import { Seo } from "../lib/head";
import { RelatedLinks } from "../components/marketing";

const FEATURES: Array<{
  icon: typeof Brain;
  title: string;
  body: string;
  highlights: string[];
}> = [
  {
    icon: Stethoscope,
    title: "Chart ingest",
    body: "Upload charts in common formats. We normalize them into resident-level snapshots with a full audit trail.",
    highlights: ["PDF · CSV · HL7", "Per-tenant storage isolation", "Manual and automated source tracking"],
  },
  {
    icon: Brain,
    title: "Clinical engine",
    body: "Rule packs for SNF, ASC, and dialysis — each flag anchored to a published citation.",
    highlights: [
      "SNF: ASCP · F605 / F757 / F881 · Boustani ACB · STOPP/START v2",
      "ASC: AAAHC pharmaceutical standards · DEA · high-alert meds · vaccine storage",
      "Dialysis: AAMI water · CMS V-tags · MBD labs · pharmacy oversight",
      "Per-rule severity, NA reasons, and allergy-aware suppression",
    ],
  },
  {
    icon: Sparkles,
    title: "AI synthesis",
    body: "AI-assisted recommendation drafting with guardrails. Clinical fact extraction is deterministic only — never LLM-based.",
    highlights: [
      "Per-tenant tuning from pharmacist edit patterns",
      "Allergy and drug interaction guardrails",
      "Outcome feedback to improve drafts over time",
      "Token budget controls per tenant",
    ],
  },
  {
    icon: ClipboardCheck,
    title: "Assessment worksheets",
    body: "Structured clinical worksheets — ACB anticholinergic burden, STOPP/START v2, Beers 2023, GDR, abx stewardship, Morse fall risk, NRS-11 pain.",
    highlights: [
      "ACB (Boustani 2008)",
      "STOPP/START v2 (O'Mahony 2015)",
      "Beers 2023 (AGS)",
      "CDC Core Elements + F881",
    ],
  },
  {
    icon: FileSignature,
    title: "Bundle render + Adobe Sign",
    body: "SBAR-formatted recommendation bundles signed by medical directors. Embedded experience with automatic status updates.",
    highlights: [
      "Professional PDF bundles",
      "Embedded signing widget",
      "Automatic status on completion",
      "Audit trail at every transition",
    ],
  },
  {
    icon: Receipt,
    title: "QuickBooks Online + time tracking",
    body: "Submit hours and auto-draft invoices. Facility-level billing metadata only — no PHI on line items.",
    highlights: [
      "OAuth connection to QuickBooks",
      "Hourly and bundle line items",
      "Safety checks on zero-hour submissions",
      "PHI-free invoice descriptions",
    ],
  },
  {
    icon: Calendar,
    title: "Calendar sync",
    body: "Two-way Google and Outlook sync. License renewal and state filing deadlines tracked automatically.",
    highlights: [
      "OAuth Google and Microsoft",
      "Renewal deadline tracking",
      "90 / 30 / expired bucketing",
      "Graceful handling of provider errors",
    ],
  },
  {
    icon: Bell,
    title: "Notifications",
    body: "Opt-in email and SMS reminders. Per-user channel preferences with compliant unsubscribe.",
    highlights: [
      "Email · SMS · in-app",
      "Review, signing, and filing reminders",
      "Per-user channel preferences",
      "CAN-SPAM compliant unsubscribe",
    ],
  },
  {
    icon: ShieldCheck,
    title: "HIPAA + audit",
    body: "Append-only audit log on every action. Tenant secrets stored in a dedicated vault. BAAs on covered subprocessors.",
    highlights: [
      "Append-only audit log",
      "Automated tenant isolation testing",
      "Encrypted tenant secret storage",
      "BAA on every covered subprocessor",
    ],
  },
  {
    icon: Building2,
    title: "State board filings",
    body: "Initial, renewal, and change-of-info filings with per-state form support and deadline tracking.",
    highlights: [
      "FL AHCA-ASC + GA DCH-ASC (more coming)",
      "PDF render and secure distribution",
      "Per-tenant document storage",
      "Renewal deadline sweeps",
    ],
  },
];

export function Features() {
  return (
    <>
      <Seo title="Consultant Pharmacist Software Features | WeConsult Rx" description="Every consultant pharmacist software capability: chart ingest, clinical rules engine, AI synthesis, assessment worksheets, e-sign bundles, QuickBooks billing, calendar sync, notifications, and state board filings." path="/features" />
      <Section className="pb-8">
        <Container>
          <div className="max-w-3xl">
            <Eyebrow>Features</Eyebrow>
            <Heading level={1} className="mt-3">
              Every capability the consultant pharmacist needs.
            </Heading>
            <Lede className="mt-6">
              We don&apos;t bolt features on. They&apos;re shipped with citations,
              automated safety checks, and HIPAA controls built in from day one.
            </Lede>
          </div>
        </Container>
      </Section>

      <Section className="pt-8">
        <Container>
          <div className="grid gap-6 lg:grid-cols-2">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <Card as="article" key={f.title}>
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-forest-50 text-forest-700">
                    <Icon className="h-6 w-6" aria-hidden />
                  </div>
                  <Heading level={3} className="mt-5">
                    {f.title}
                  </Heading>
                  <p className="mt-3 text-ink-600">{f.body}</p>
                  <ul className="mt-4 space-y-1.5 text-sm text-ink-700">
                    {f.highlights.map((h) => (
                      <li key={h} className="flex items-start gap-2">
                        <span className="text-forest-700">•</span>
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              );
            })}
          </div>

          <div className="mt-16 text-center">
            <LinkButton to="/demo" size="lg">
              See it in your workflow
            </LinkButton>
          </div>
        </Container>
      </Section>
      <RelatedLinks
        items={[
          { to: "/medication-regimen-review-software", label: "MRR software", desc: "The monthly review, end to end." },
          { to: "/consultant-pharmacist-software", label: "Consultant pharmacist software", desc: "One platform: SNF, ASC, dialysis." },
          { to: "/survey-readiness", label: "Survey readiness", desc: "F-tag & GDR documentation, automated." },
        ]}
      />
    </>
  );
}
